/**
 * «Ваш личный гид»: the link from a saved plan back into the guide (INV-PH-166).
 *
 * **The token is the graph's, and the client never reads it.** `graphs/peterhof_v2/return_link.py` writes it
 * on the plan card (`TripPlan.trip_link`): the template's id or the stops' museum ids, the visit day, the
 * party — no thread, no visitor, no words, because a PDF is forwarded. This module only puts it into an
 * address and takes it out again; the guide's CODE rebuilds the plan from it (a structured action,
 * `a2ui/actions.ts` `dispatchTripLink` — not a line for a model to read).
 *
 * **The address is the one the guide was opened at** (`origin` + `pathname`): dev and prod differ and the site
 * may live under a sub-path, so nothing here names a domain. `graph=peterhof_v2` rides with the token — the
 * only graph that reads it — so a build whose landing graph is another one still opens the right guide.
 *
 * Short on purpose: the link goes into a QR code and a calendar field (`LINK_MAX`).
 */

import type { TripPlanProps } from '../catalog/generated/schemas';

export const TRIP_PARAM = 'trip';
export const GRAPH_PARAM = 'graph';
/** The graph that rebuilds a plan from the token (`sessionStore` `KNOWN_GRAPHS`). */
export const RETURN_GRAPH = 'peterhof_v2';
/** The visitor's bubble for the action — what they see; the graph reads the action, never this text. */
export const RETURN_LINE = 'Открыл свой план по ссылке';
/** A link longer than this is not made: it would not stay a small QR code. */
export const LINK_MAX = 600;

/** What the graph writes (`return_link.TOKEN_MAX`): base64url, nothing else. */
const TOKEN = /^[A-Za-z0-9_-]{8,480}$/;

export type Place = Pick<Location, 'origin' | 'pathname'>;

export function isToken(value: unknown): value is string {
  return typeof value === 'string' && TOKEN.test(value);
}

/** `https://host/sub/path/?graph=peterhof_v2&trip=…`, or `null` — no token, or too long for a QR. */
export function tripLinkUrl(token: string | null | undefined, place: Place): string | null {
  if (!isToken(token)) return null;
  const url = `${place.origin}${place.pathname}?${GRAPH_PARAM}=${RETURN_GRAPH}&${TRIP_PARAM}=${token}`;
  return url.length <= LINK_MAX ? url : null;
}

/** The link of the plan card, or `null` when the card has no token (the Agent's plans, a plan with no day). */
export function planLink(
  props: Pick<TripPlanProps, 'trip_link'>,
  place: Place | undefined = typeof window === 'undefined' ? undefined : window.location,
): string | null {
  return place ? tripLinkUrl(props.trip_link, place) : null;
}

/** The token of an opened link (`location.search`), or `null`. Anything that is not a token is not one. */
export function readTripToken(search: string): string | null {
  const value = new URLSearchParams(search).get(TRIP_PARAM);
  return isToken(value) ? value : null;
}

/** The same address without the token — kept `graph`, other parameters and the hash — so a reload does not open the link again. */
export function withoutTripToken(search: string, pathname: string, hash = ''): string {
  const params = new URLSearchParams(search);
  params.delete(TRIP_PARAM);
  const rest = params.toString();
  return `${pathname}${rest ? `?${rest}` : ''}${hash}`;
}

let opened: string | null | undefined;

/**
 * The token this page was opened with — read ONCE and taken out of the address bar at once, so neither a reload
 * nor the visitor copying the address carries a link that was already used. Later calls return the same token
 * (React may run an effect twice); `consumeTripToken` is what spends it.
 */
export function openedTripToken(win: Pick<Window, 'location' | 'history'> | undefined = typeof window === 'undefined' ? undefined : window): string | null {
  if (opened !== undefined || !win) return opened ?? null;
  opened = readTripToken(win.location.search);
  if (opened) {
    try {
      win.history.replaceState(win.history.state, '', withoutTripToken(win.location.search, win.location.pathname, win.location.hash));
    } catch {
      /* an address that cannot be rewritten still opens the link once, `consumeTripToken` is the guard */
    }
  }
  return opened;
}

let spent = false;

/** → the token to send, once per page load; `null` after it was spent or when the page had none. */
export function consumeTripToken(): string | null {
  const token = openedTripToken();
  if (!token || spent) return null;
  spent = true;
  return token;
}

/** The link is spent only when the sender took it (offline, a turn running): otherwise it stays for the next try. */
export function unconsumeTripToken(): void {
  spent = false;
}

/** For tests: forget the page's token and that it was spent. */
export function resetTripToken(): void {
  opened = undefined;
  spent = false;
}
